// pdfToImages.js — convert PDF bytes -> base64 PNG (หน้าละรูป)
// ใช้ pdfjs-dist render บน canvas แล้ว encode เป็น PNG base64
//
// ใช้คู่กับ printAgent.printImage() เพื่อให้ C# agent ปริ้นผ่าน System.Drawing.Printing
// ตรงสู่ printer driver — ไม่ต้องพึ่ง Adobe / Edge

import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

// worker source — โหลดจาก CDN ครั้งแรก แล้ว browser cache เอง
// ใช้ legacy build ให้ compat กับ Vue 2 + Webpack 4
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.js`;

/**
 * แปลง PDF เป็นรายการ base64 PNG ทีละหน้า
 *
 * **เกี่ยวกับ aspect ratio**:
 * - ถ้าระบุ targetWidth + targetHeight → output canvas เป็นขนาดนั้น
 *   เนื้อหา PDF จะถูก scale แบบ "fit" (รักษาสัดส่วน) วางชิด **top-left**
 *   ส่วนที่เหลือเป็นพื้นที่ขาว — ใช้ตอนกระดาษไม่ตรงสัดส่วน PDF เพื่อกัน distortion
 * - ถ้าไม่ระบุ → ใช้ render scale อย่างเดียว (output ได้สัดส่วนเดียวกับ PDF page)
 *
 * @param {Uint8Array|ArrayBuffer} pdfBytes
 * @param {object} [options]
 * @param {number} [options.scale=3]  render scale (ใช้เมื่อไม่ระบุ targetWidth/Height)
 * @param {number} [options.targetWidth]   pixel — บังคับ canvas width (ตรงสัดส่วนกระดาษ)
 * @param {number} [options.targetHeight]  pixel — บังคับ canvas height
 * @param {number} [options.paddingRatio=0]  สัดส่วน padding รอบ image (0-0.1)
 *                                            ใช้กัน hardware margin ของ printer clip ขอบ
 * @param {string} [options.mimeType="image/png"]  "image/png" หรือ "image/jpeg"
 * @param {number} [options.jpegQuality=0.95]
 * @returns {Promise<string[]>}
 */
export async function pdfToImages(pdfBytes, options = {}) {
  const baseScale = options.scale || 3;
  const targetW = options.targetWidth;
  const targetH = options.targetHeight;
  const paddingRatio = options.paddingRatio || 0;
  const mimeType = options.mimeType || "image/png";
  const jpegQuality = options.jpegQuality || 0.95;
  const useTarget = targetW && targetH;

  const data = pdfBytes instanceof Uint8Array
    ? new Uint8Array(pdfBytes)
    : new Uint8Array(pdfBytes);

  const loadingTask = pdfjsLib.getDocument({ data });
  const pdfDoc = await loadingTask.promise;

  const images = [];
  for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
    const page = await pdfDoc.getPage(pageNum);

    let canvasW, canvasH, renderViewport;

    // ขนาด padding (กัน hardware margin clip ขอบ)
    const padX = useTarget ? Math.floor(targetW * paddingRatio) : 0;
    const padY = useTarget ? Math.floor(targetH * paddingRatio) : 0;

    if (useTarget) {
      // คำนวณ scale แบบ fit (รักษาสัดส่วน) ให้ content พอดี (target - padding*2)
      const effectiveW = targetW - 2 * padX;
      const effectiveH = targetH - 2 * padY;
      const natural = page.getViewport({ scale: 1 });
      const fitScale = Math.min(effectiveW / natural.width, effectiveH / natural.height);
      renderViewport = page.getViewport({ scale: fitScale });
      canvasW = targetW;
      canvasH = targetH;
    } else {
      renderViewport = page.getViewport({ scale: baseScale });
      canvasW = Math.ceil(renderViewport.width);
      canvasH = Math.ceil(renderViewport.height);
    }

    const canvas = document.createElement("canvas");
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext("2d");

    // พื้นหลังขาว (PDF บางตัวไม่มี bg, thermal ปริ้น transparent ไม่ได้
    // + ใช้เป็น letterbox ตอน canvas ใหญ่กว่า content + padding รอบขอบ)
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvasW, canvasH);

    // จัดวาง: ภายใน effective area (padding รอบ) -> center แนวนอน + top-align แนวตั้ง
    let offsetX = 0;
    let offsetY = 0;
    if (useTarget) {
      const effectiveW = canvasW - 2 * padX;
      offsetX = padX + Math.max(0, Math.floor((effectiveW - renderViewport.width) / 2));
      offsetY = padY; // ชิดด้านบน (เผื่อ padding กัน clip)
    }

    await page.render({
      canvasContext: ctx,
      viewport: renderViewport,
      transform: (offsetX || offsetY) ? [1, 0, 0, 1, offsetX, offsetY] : undefined,
      intent: "print",
    }).promise;

    const dataUrl = mimeType === "image/jpeg"
      ? canvas.toDataURL(mimeType, jpegQuality)
      : canvas.toDataURL(mimeType);

    images.push(dataUrl.split(",")[1]);
  }

  await pdfDoc.cleanup();
  await pdfDoc.destroy();

  return images;
}

export default pdfToImages;
